import type { FeedOutput, FeedStatus } from '../api/types';
import { UNLABELLED } from '../api/types';
import { LabelFilterMenu } from './LabelFilterMenu';

interface Props {
  value: FeedOutput;
  onChange: (patch: Partial<FeedOutput>) => void;
  status: FeedStatus | undefined;
  canEdit: boolean;   // org owner or admin — members see the fields read-only
}

export default function FeedProductSelection({ value, onChange, status, canEdit }: Props) {
  const dis = !canEdit;
  return (
    <div className="rounded-xl border border-ff-sand/60 bg-white/40 p-3 space-y-2">
      <p className="text-[12px] font-semibold text-ff-forest">Which products go in the feed</p>
      <div>
        <label className="ff-label" htmlFor="feed-output-render-gate">Render quality</label>
        <select id="feed-output-render-gate" value={value.render_gate} disabled={dis}
          onChange={(e) => onChange({ render_gate: e.target.value as FeedOutput['render_gate'] })}
          className="ff-input text-sm" data-testid="feed-output-render-gate">
          <option value="not_failed">Any render not failed by QA (default)</option>
          <option value="qa_pass">Only renders that passed QA (bot or human)</option>
          <option value="human_pass">Only renders a human marked as pass</option>
        </select>
        <p className="mt-1 text-[11px] text-ff-mute">Unreviewed renders count as usable only with the default.</p>
      </div>
      <div data-testid="feed-output-labels">
        <label className="ff-label">Labels</label>
        <div className={dis ? 'pointer-events-none opacity-60' : undefined}>
          <LabelFilterMenu
            labels={status?.labels ?? []}
            value={{ include: value.require_labels, exclude: value.exclude_labels }}
            onChange={(f) => onChange({
              require_labels: f.include.filter((l) => l !== UNLABELLED),
              exclude_labels: f.exclude.filter((l) => l !== UNLABELLED),
            })}
          />
        </div>
        <p className="mt-1 text-[11px] text-ff-mute">
          Show = only products carrying one of these labels; hide = never these. Labels are set in the QA
          gallery. The menu's Unlabelled row has no effect here — it filters the gallery view only, and
          toggling it here snaps back on save.
        </p>
      </div>
      <p className="text-[11px] font-mono" data-testid="feed-output-qualifying">
        {status?.qualifying
          ? `${status.qualifying.qualifying} of ${status.qualifying.current_renders} current renders qualify with the SAVED settings`
          : 'Qualifying count unavailable'}
      </p>
    </div>
  );
}
